/**
 * Returns the shopper to the product image when they change a variant on mobile.
 *
 * On mobile the gallery sits above the details, so by the time someone has
 * scrolled down to the swatches the image is off-screen. Changing a variant
 * swaps it silently and the change goes unnoticed. This scrolls the gallery
 * back into view so the new image is actually seen.
 *
 * Only on mobile: on desktop the gallery is beside the form and already
 * visible, where scrolling would be an unprompted jump.
 *
 * Standalone (no @theme/component import) so a module failure can't take the
 * product page with it — without this script the page simply behaves as before.
 */
(function () {
  const MOBILE_QUERY = '(max-width: 749px)';

  /** Below this much of the gallery on screen, it counts as "not looking at it". */
  const MIN_VISIBLE_FRACTION = 0.4;

  /** Breathing room between the header and the gallery once scrolled. */
  const GAP = 8;

  /**
   * The header is only sometimes sticky (`sticky="always"`, or `scroll-up`
   * while active), so its offset is measured live rather than assumed.
   */
  function stickyHeaderOffset() {
    const header = document.querySelector('.header-section');
    if (!header) return 0;

    const { position } = getComputedStyle(header);
    if (position !== 'sticky' && position !== 'fixed') return 0;

    return header.offsetHeight;
  }

  function visibleFraction(element) {
    const rect = element.getBoundingClientRect();
    if (!rect.height) return 1;

    const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return Math.max(0, visible) / rect.height;
  }

  /** Queried fresh each time: media-gallery replaces itself on variant update. */
  function revealGallery(root) {
    const gallery = root.querySelector('media-gallery');
    if (!gallery) return;

    // Already on screen — the shopper can see the change, so leave the scroll alone.
    if (visibleFraction(gallery) >= MIN_VISIBLE_FRACTION) return;

    const top = window.scrollY + gallery.getBoundingClientRect().top - stickyHeaderOffset() - GAP;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  function init(root) {
    let lastMediaId = root.dataset.tdmFeaturedMedia || '';
    let frame = 0;

    root.addEventListener('variant:update', (event) => {
      const mediaId = event.detail?.resource?.featured_media?.id;

      // Two variants can share an image. Scrolling up to an identical picture
      // reads as a glitch, so track the featured media and skip those. When the
      // event carries no media id we fail open and scroll.
      const sameImage = mediaId != null && String(mediaId) === lastMediaId;
      if (mediaId != null) lastMediaId = String(mediaId);

      if (sameImage) return;
      if (!matchMedia(MOBILE_QUERY).matches) return;

      // media-gallery swaps itself in a listener on the section, which is a
      // further ancestor than this one and so runs after. Deferring a frame
      // lets that land, and puts the measurement after layout.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => revealGallery(root));
    });
  }

  document.querySelectorAll('[data-tdm-variant-scroll]').forEach(init);
})();
